import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

// Nav link sets rendered by the top bar. The org tools live at
// `org.go-ev.net` (flat paths), and the main site at `go-ev.net`. On the
// main domain the legacy /org path still scopes the navbar to the org set
// so old bookmarks keep working until the redirect fires.
type NavLink = { label: string; to: string };

// Same-subdomain check as App.tsx. Kept inline (rather than imported) to
// avoid a circular import via App -> Navbar.
const isOrgHost =
  typeof window !== 'undefined' &&
  window.location.hostname.split('.')[0] === 'org';

const DEFAULT_NAV_LINKS: readonly NavLink[] = [
  // Hidden until the live leaderboard / map are ready to be exposed publicly
  // again. The routes themselves are still wired up in App.tsx.
  // { label: 'Leaderboard', to: '/' },
  // { label: 'Map',         to: '/map' },
  { label: 'Results',     to: '/results' },
  { label: 'Comparison',  to: '/comparison' },
];

// On the org subdomain, everything is rooted; on the main domain we keep
// the old /org/* paths so the in-page Router can redirect them out.
const ORG_NAV_LINKS: readonly NavLink[] = isOrgHost
  ? [
      { label: 'Live Board', to: '/' },
      { label: 'Scoring',    to: '/scoring' },
      { label: 'Location',   to: '/location' },
    ]
  : [
      { label: 'Live Board', to: '/org' },
      { label: 'Scoring',    to: '/org/scoring' },
      { label: 'Location',   to: '/org/location' },
    ];

export default function Navbar() {
  const location = useLocation();
  const isOrg       = isOrgHost || location.pathname.startsWith('/org');
  const navLinks    = isOrg ? ORG_NAV_LINKS : DEFAULT_NAV_LINKS;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return(
    <Box sx={{ 
      flexGrow: 1, 
      width: '100vw',
      margin: 0,
      padding: 0,
      position: 'relative',
      left: '50%',
      right: '50%',
      marginLeft: '-50vw',
      marginRight: '-50vw'
    }}>
      <AppBar position="static" sx={{ 
        backgroundColor: 'dimgrey', 
        paddingTop: { xs: '4px', sm: '8px' },
        width: '100%',
        maxWidth: '100vw',
        margin: 0,
        padding: 0,
        position: 'relative',
        left: 0,
        right: 0
      }}>
        <Toolbar sx={{ 
          width: '100%',
          padding: { xs: '0 4px', sm: '0 16px', md: '0 24px' },
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: { xs: 0.5, sm: 2 },
          minHeight: { xs: '48px', sm: '64px' },
          margin: 0
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.5, sm: 2 },
            flexWrap: 'wrap',
            margin: 0,
            padding: 0
          }}>
            {!isMobile ? (
              <>
                {navLinks.map((link) => (
                  <Typography
                    key={link.to}
                    variant="h6"
                    component="div"
                    sx={{
                      color: 'white',
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    <Link to={link.to} style={{ color: 'white', textDecoration: 'none' }}>
                      {link.label}
                    </Link>
                  </Typography>
                ))}
              </>
            ) : (
              <IconButton
                size="small"
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleMenu}
                sx={{ 
                  color: 'white',
                  margin: 0,
                  padding: 0
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              sx={{
                '& .MuiPaper-root': {
                  minWidth: '150px'
                }
              }}
            >
              {navLinks.map((link) => (
                <MenuItem
                  key={link.to}
                  onClick={handleClose}
                  component={Link}
                  to={link.to}
                >
                  {link.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            marginRight: { xs: '4px', sm: '16px', md: '40px' },
            margin: 0,
            padding: 0
          }}>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
}


